import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import {
  Table,
  Select,
  Input,
  Tooltip
} from 'antd';
import { notificationsAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import Delta from '../../shared-components/charts/Delta';
import HintIcon from '../../shared-components/charts/HintIcon';
import ComparisonHint from '../../shared-components/charts/ComparisonHint';
import api from '../../../api/api';
import { thousand } from '../../../functions';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function MergedSalesWeeklySummary() {
  const { t } = useTranslation();
  const { Option, OptGroup } = Select;

  // Oldest first so the row reads left-to-right in time order. `total` differs
  // between the two tables: the product rows expose totalSales*, the per-store
  // rows expose sales*.
  const WEEKS = [
    { key: 'FourWeeksAgo', label: t('four_weeks_ago') },
    { key: 'ThreeWeeksAgo', label: t('three_weeks_ago') },
    { key: 'TwoWeeksAgo', label: t('two_weeks_ago') },
    { key: 'OneWeekAgo', label: t('one_weeks_ago') },
    { key: 'ThisWeek', label: t('this_week'), partial: true },
  ];

  const sortFilters = [
    { name: `${t('name')} A-Z`, type: 1 },
    { name: `${t('name')} Z-A`, type: 0 },
    { name: `${t('category')} A-Z`, type: 3 },
    { name: `${t('category')} Z-A`, type: 2 },
  ];
  const salesSorts = [
    { name: `${t('this_week_sales')} ${t('desc')}`, type: 4 },
    { name: `${t('one_week_ago_sales')} ${t('desc')}`, type: 8 },
    { name: `${t('two_week_ago_sales')} ${t('desc')}`, type: 12 },
    { name: `${t('three_week_ago_sales')} ${t('desc')}`, type: 16 },
    { name: `${t('four_week_ago_sales')} ${t('desc')}`, type: 20 },
  ];
  const avgSorts = [
    { name: `${t('this_week_avg')} ${t('desc')}`, type: 6 },
    { name: `${t('one_week_ago_avg')} ${t('desc')}`, type: 10 },
    { name: `${t('two_week_ago_avg')} ${t('desc')}`, type: 14 },
    { name: `${t('three_week_ago_avg')} ${t('desc')}`, type: 18 },
    { name: `${t('four_week_ago_avg')} ${t('desc')}`, type: 22 },
  ];

  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [loading, setLoading] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSortFilter, setSelectedSortFilter] = useState(4);
  const [selectedCategory, setSelectedCategory] = useState(null);

  function getCategories() {
    setLoadingCategories(true);
    api.get('window/summaries/sales/weekly/cateogries')
      .then((response) => {
        setLoadingCategories(false);
        if (response.data.success) {
          setCategories(response.data.data);
          if (selectedCategory == null) {
            setSelectedCategory(_.filter(response.data.data, (c) => c)[0]);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingCategories(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function search(value, page) {
    if (selectedCategory == null) {
      return;
    }
    setLastSearchTerm(value);
    setCurrentPage(page);
    setLoading(true);
    api
      .post(
        'window/summaries/sales/weekly/search',
        {
          Query: value,
          Size: pageSize,
          Page: page,
          Sort: selectedSortFilter,
          Category: selectedCategory
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSearchResult(response.data.data);
        } else {
          setSearchResult({
            ...searchResult,
            items: []
          });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function onSearch(value) {
    search(value, 0);
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(lastSearchTerm, page - 1);
    } else {
      setPageSize(size);
    }
  };

  useEffect(() => {
    search(lastSearchTerm, 0);
  }, [selectedSortFilter, selectedCategory, pageSize]);

  useEffect(() => {
    setSelectedSortFilter(4);
    getCategories();
  }, []);

  // `totalField` differs between the product table and the per-store table.
  function weekColumns(leading, totalField) {
    return [
      leading,
      ..._.map(WEEKS, (week, i) => ({
        title: (
          <div className="text-right">
            {week.label}
            {week.partial && (
              <div className="text-xs font-normal opacity-70">{t('week_to_date')}</div>
            )}
          </div>
        ),
        key: `sales${week.key}`,
        align: 'right',
        render: (row) => {
          const total = num(row[`${totalField}${week.key}`]);
          const avg = row[`avgTotal${week.key}`];
          const previous = i > 0 ? num(row[`${totalField}${WEEKS[i - 1].key}`]) : null;
          return (
            <div className="tabular-nums">
              <div className="font-semibold text-ink">{thousand(total)}</div>
              {avg == null ? (
                // The current week is still running, so a per-day average from a
                // partial total would read as a real slowdown. Left blank on
                // purpose rather than shown as a smaller number.
                <div className="text-xs text-muted">&nbsp;</div>
              ) : (
                <div className="text-xs text-muted">{num(avg).toFixed(0)} / {t('daily')}</div>
              )}
              {/* No delta on the partial week -- comparing a part-week total
                  against a full week would always look like a collapse. */}
              {!week.partial && previous !== null && (
                <Tooltip
                  styles={{ root: { maxWidth: 'none' } }}
                  placement="left"
                  title={(
                    <ComparisonHint
                      currentLabel={week.label}
                      currentValue={total}
                      previousLabel={WEEKS[i - 1].label}
                      previousValue={previous}
                    />
                  )}
                >
                  <span className="group inline-flex cursor-help items-center gap-1">
                    <Delta current={total} previous={previous} />
                    <HintIcon />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      })),
    ];
  }

  return (
    <MainPage>
      <div className="flex mt-4">
        <H1 text={t('sales_summary_window')} />
      </div>
      <div className="lg:flex items-center justify-center">
        <Input.Search
          placeholder={`${t('search')}...`}
          allowClear
          disabled={loadingCategories}
          className="mt-4"
          enterButton={t('search')}
          size="large"
          onClear={() => onSearch('')}
          onSearch={(val) => onSearch(val)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 md:space-x-2 lg:flex">
          <Select
            loading={loadingCategories}
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 mr-1 md:mr-0 ml-0 lg:ml-2"
            value={selectedCategory}
            disabled={loadingCategories}
            onChange={(value) => setSelectedCategory(value)}
          >
            {_.map(categories, (category) => (
              <Option
                key={category ?? '[no_data]'}
                value={category ?? '[no_data]'}
                className="text-sm flex items-center text-start"
              >
                {category ?? t('[no_data]')}
              </Option>
            ))}
          </Select>
          <Select
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-56 ml-1 md:ml-0"
            value={selectedSortFilter}
            disabled={loadingCategories}
            onChange={(value) => setSelectedSortFilter(value)}
          >
            <OptGroup label={t('product')}>
              {_.map(sortFilters, (filter) => (
                <Option key={filter.type} value={filter.type}>{filter.name}</Option>
              ))}
            </OptGroup>
            <OptGroup label={t('total')}>
              {_.map(salesSorts, (filter) => (
                <Option key={filter.type} value={filter.type}>{filter.name}</Option>
              ))}
            </OptGroup>
            <OptGroup label={t('daily')}>
              {_.map(avgSorts, (filter) => (
                <Option key={filter.type} value={filter.type}>{filter.name}</Option>
              ))}
            </OptGroup>
          </Select>
        </div>
      </div>
      <div className="mt-4">{t('found_in_search')}: {searchResult?.items ? thousand(searchResult?.total) : 0}</div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        size="small"
        className="mt-2"
        scroll={{ x: 'max-content' }}
        loading={loading}
        rowKey="itemCode"
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: searchResult?.total,
          onChange: onTableChange
        }}
        dataSource={searchResult?.items}
        columns={weekColumns(
          {
            title: t('product'),
            key: 'name',
            align: 'left',
            render: (row) => (
              <div style={{ minWidth: '11rem' }}>
                <div className="text-sm font-semibold text-ink">{row.name}</div>
                <div className="text-xs text-muted tabular-nums">{row.itemCode}</div>
              </div>
            ),
          },
          'totalSales'
        )}
        expandable={{
          expandRowByClick: false,
          rowExpandable: () => true,
          onExpand: (expanded, record) => {
            if (expanded && !record.loadedStores && !record.loadingStores) {
              const temp = { ...searchResult };
              for (let i = 0; i < temp.items.length; i++) {
                if (temp.items[i].itemCode === record.itemCode) {
                  temp.items[i].loadingStores = true;
                }
              }
              setSearchResult(temp);
              api.get(`window/summaries/sales/weekly/bystore?itemCode=${record.itemCode}`)
                .then((response) => {
                  const temp2 = { ...searchResult };
                  if (response.data.success) {
                    for (let i = 0; i < temp2.items.length; i++) {
                      if (temp2.items[i].itemCode === record.itemCode) {
                        temp2.items[i].stores = response.data.data;
                      }
                    }
                  } else {
                    setNotifications([
                      ...notifications,
                      {
                        title: t('error'),
                        description: response.data.error,
                        error: true,
                      },
                    ]);
                  }
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  setSearchResult(temp2);
                })
                .catch((error) => {
                  const temp2 = { ...searchResult };
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  setSearchResult(temp2);
                  setNotifications([
                    ...notifications,
                    {
                      title: t('error'),
                      description: error.message,
                      error: true,
                    },
                  ]);
                });
            }
          },
          expandedRowRender: (record) => (
            <div className="py-2">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                {t('store')}
              </div>
              <Table
                size="small"
                bordered
                pagination={false}
                scroll={{ x: 'max-content' }}
                rowKey={(row) => `${record.itemCode}-${row.source}-${row.storeName}`}
                loading={record.loadingStores}
                dataSource={record?.stores}
                locale={{ emptyText: t('no_data') }}
                columns={weekColumns(
                  {
                    title: t('store'),
                    key: 'store',
                    align: 'left',
                    render: (row) => (
                      <div style={{ minWidth: '11rem' }}>
                        <div className="text-sm font-semibold text-ink">{row.storeName}</div>
                        <div className="text-xs text-muted">{row.source}</div>
                      </div>
                    ),
                  },
                  'sales'
                )}
              />
            </div>
          )
        }}
      />
    </MainPage>
  );
}

export default MergedSalesWeeklySummary;
